import { useMemo } from 'react';
import classNames from 'classnames';
import { ColumnDef, Row, SortingState } from '@tanstack/react-table';
import { Checkbox, Icon, Icons } from 'Components/Primitives';
import { TableAction, TableColumn } from 'Components/Table/Table.types';
import { TableActionsCell } from 'Components/Table/TableActionsCell/TableActionsCell';

export const useTableColumnsDefs = function <T>(params: {
  columns: TableColumn<T>[];
  actions: TableAction<T>[] | undefined;
  withRowSelect: boolean;
  getTableSortingState(): SortingState | undefined;
}): ColumnDef<T>[] {
  const { columns, actions, withRowSelect, getTableSortingState } = params;

  const columnsDef = useMemo((): ColumnDef<T>[] => {
    const cols: ColumnDef<T>[] = columns.map((col, index) => {
      return {
        id: col.id,
        accessorKey: col.accessor.join('.'),
        maxSize: col.maxSize,
        ...(col.sort && {
          sortingFn: (rowA: Row<T>, rowB: Row<T>, columnId: string) => {
            const sortingState = getTableSortingState();
            if (col.sort && sortingState && sortingState.length > 0 && sortingState[0].id === columnId) {
              return col.sort(rowA.original, rowB.original, sortingState[0].desc ? 'descend' : 'ascend');
            }
            return 0;
          }
        }),
        header: () => <span>{col.header}</span>,
        cell: ({ row, getValue }) => {
          return index === 0 ? (
            <div className="table-cell-wrapper" style={{ paddingLeft: `${row.depth * 32}px` }}>
              {row.getCanExpand() ? (
                <>
                  <button {...{ onClick: row.getToggleExpandedHandler() }} className="expand-row-button">
                    <Icon
                      icon={Icons.ChevronRight}
                      className={classNames('expand-row-icon', row.getIsExpanded() && 'expanded')}
                    />
                  </button>{' '}
                  {col.render(getValue(), row.original)}
                </>
              ) : (
                <> {col.render(getValue(), row.original)}</>
              )}
            </div>
          ) : (
            <>{col.render(getValue(), row.original)}</>
          );
        }
      };
    });

    const rowSelectCol: ColumnDef<T>[] = withRowSelect
      ? [
          {
            id: 'row-select',
            accessorKey: 'row-select',
            header: ({ table }) => (
              <Checkbox
                {...{
                  checked: table.getIsAllRowsSelected(),
                  indeterminate: table.getIsSomeRowsSelected(),
                  onChange: table.getToggleAllRowsSelectedHandler()
                }}
              />
            ),
            cell: ({ row }) => (
              <div className="px-1">
                <Checkbox
                  {...{
                    checked: row.getIsSelected(),
                    disabled: !row.getCanSelect(),
                    indeterminate: row.getIsSomeSelected(),
                    onChange: row.getToggleSelectedHandler()
                  }}
                />
              </div>
            )
          }
        ]
      : [];

    const actionCol: ColumnDef<T>[] =
      actions && actions.length > 0
        ? [
            {
              id: 'actions',
              accessorKey: 'actions',
              header: () => <></>,
              cell: ({ row }) => <TableActionsCell record={row.original} actions={actions} />
            }
          ]
        : [];

    return rowSelectCol.concat(cols).concat(actionCol);
  }, [columns, actions, withRowSelect, getTableSortingState]);

  return columnsDef;
};
